import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Button, Spinner, Alert, Badge, Form, InputGroup, FormControl, Tabs, Tab } from 'react-bootstrap';
import { adminAPI } from '../../services/apiService';
import { getTranslatedError } from '../../utils/errorTranslator';
import { BellOutlined, ReloadOutlined, PlusOutlined, EditOutlined, SendOutlined, FileTextOutlined } from '@ant-design/icons';
import { message, Select } from 'antd';
import StackedModal from '../common/StackedModal';

const Modal = StackedModal;

const Notifications = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [templates, setTemplates] = useState([]);
  const [history, setHistory] = useState([]);
  const [users, setUsers] = useState([]); // Состояние для списка пользователей
  const [activeTab, setActiveTab] = useState('send');
  
  // Модальные окна
  const [showSendModal, setShowSendModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  
  // Формы
  const [sendForm, setSendForm] = useState({
    userIds: [], // Массив ID пользователей
    title: '',
    message: '',
    type: 'info',
  });
  const [bulkForm, setBulkForm] = useState({
    filter: 'all',
    title: '',
    message: '',
    type: 'info',
  });
  const [templateForm, setTemplateForm] = useState({
    name: '',
    title: '',
    message: '',
    variables: '',
  });

  useEffect(() => {
    loadAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [templatesData, historyData, usersData] = await Promise.all([
        adminAPI.getNotificationTemplates().catch(() => []),
        adminAPI.getNotificationHistory().catch(() => ({ history: [] })),
        adminAPI.getUsersList({ limit: 1000 }).catch(() => ({ users: [] })), // Загружаем пользователей
      ]);
      
      if (Array.isArray(templatesData)) setTemplates(templatesData);
      else if (templatesData.templates) setTemplates(templatesData.templates);
      
      if (historyData.history) setHistory(historyData.history);
      else if (Array.isArray(historyData)) setHistory(historyData);

      if (usersData.users) setUsers(usersData.users);
    } catch (err) {
      setError('Ошибка при загрузке данных: ' + getTranslatedError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    try {
      const userIds = Array.isArray(sendForm.userIds) ? sendForm.userIds : [];
      if (userIds.length === 0) {
        message.error('Выберите хотя бы одного пользователя');
        return;
      }
      
      await adminAPI.sendNotification({
        userIds,
        title: sendForm.title,
        message: sendForm.message,
        type: sendForm.type,
      });
      
      message.success(`Уведомление отправлено ${userIds.length} пользователям`);
      setShowSendModal(false);
      setSendForm({ userIds: [], title: '', message: '', type: 'info' });
      loadAllData();
    } catch (err) {
      message.error('Ошибка: ' + getTranslatedError(err));
    }
  };

  const handleBulkSend = async () => {
    try {
      if (!bulkForm.title || !bulkForm.message) {
        message.error('Заполните заголовок и сообщение');
        return;
      }
      
      await adminAPI.bulkSendNotifications({
        filter: bulkForm.filter,
        title: bulkForm.title,
        message: bulkForm.message,
        type: bulkForm.type,
      });
      
      message.success('Массовая рассылка запущена');
      setShowBulkModal(false);
      setBulkForm({ filter: 'all', title: '', message: '', type: 'info' });
      loadAllData();
    } catch (err) {
      message.error('Ошибка: ' + getTranslatedError(err));
    }
  };

  const handleSaveTemplate = async () => {
    try {
      if (!templateForm.name || !templateForm.title || !templateForm.message) {
        message.error('Заполните все обязательные поля');
        return;
      }
      
      if (editingTemplate) {
        await adminAPI.updateNotificationTemplate(editingTemplate.id, templateForm);
        message.success('Шаблон обновлен');
      } else {
        await adminAPI.createNotificationTemplate(templateForm);
        message.success('Шаблон создан');
      }
      
      setShowTemplateModal(false);
      setEditingTemplate(null);
      setTemplateForm({ name: '', title: '', message: '', variables: '' });
      loadAllData();
    } catch (err) {
      message.error('Ошибка: ' + getTranslatedError(err));
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name || '',
      title: template.title || '',
      message: template.message || '',
      variables: template.variables ? template.variables.join(', ') : '',
    });
    setShowTemplateModal(true);
  };

  const handleUseTemplate = (template) => {
    setSendForm({
      ...sendForm,
      title: template.title || '',
      message: template.message || '',
    });
    setShowSendModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleString('ru-RU');
    } catch {
      return dateString;
    }
  };

  const getTypeBadge = (type) => {
    const variants = {
      info: 'primary',
      success: 'success',
      warning: 'warning',
      error: 'danger',
    };
    return variants[type] || 'secondary';
  };

  return (
    <div>
      {/* Заголовок */}
      <Card className="mb-2">
        <Card.Header className="p-2">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0" style={{ fontSize: '16px' }}>
              <BellOutlined className="me-2" />
              Управление уведомлениями
            </h6>
            <div className="d-flex gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  setEditingTemplate(null);
                  setTemplateForm({ name: '', title: '', message: '', variables: '' });
                  setShowTemplateModal(true);
                }}
                style={{ fontSize: '12px', padding: '4px 8px' }}
              >
                <PlusOutlined /> Создать шаблон
              </Button>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={loadAllData}
                disabled={loading}
                style={{ fontSize: '12px', padding: '4px 8px' }}
              >
                <ReloadOutlined /> Обновить
              </Button>
            </div>
          </div>
        </Card.Header>
      </Card>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-2">
          {error}
        </Alert>
      )}

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-2">
        <Tab
          eventKey="send"
          title={
            <span className="d-flex align-items-center gap-2">
              <SendOutlined />
              Отправка
            </span>
          }
        >
          <Row className="mb-2">
            <Col md={6}>
              <Card>
                <Card.Header className="p-2">
                  <h6 className="mb-0" style={{ fontSize: '14px' }}>Отправить уведомление</h6>
                </Card.Header>
                <Card.Body className="p-2">
                  <Button
                    variant="primary"
                    className="w-100 mb-2"
                    onClick={() => setShowSendModal(true)}
                  >
                    <SendOutlined /> Отправить пользователям
                  </Button>
                  <Button
                    variant="success"
                    className="w-100"
                    onClick={() => setShowBulkModal(true)}
                  >
                    <SendOutlined /> Массовая рассылка
                  </Button>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card>
                <Card.Header className="p-2">
                  <h6 className="mb-0" style={{ fontSize: '14px' }}>Шаблоны ({templates.length})</h6>
                </Card.Header>
                <Card.Body className="p-2">
                  {templates.length === 0 ? (
                    <Alert variant="info" className="mb-0" style={{ fontSize: '12px' }}>
                      Нет шаблонов
                    </Alert>
                  ) : (
                    <div className="table-responsive">
                      <Table striped bordered hover size="sm">
                        <thead>
                          <tr>
                            <th style={{ fontSize: '12px' }}>Название</th>
                            <th style={{ fontSize: '12px' }}>Действия</th>
                          </tr>
                        </thead>
                        <tbody>
                          {templates.map((template) => (
                            <tr key={template.id}>
                              <td style={{ fontSize: '12px' }}>{template.name}</td>
                              <td>
                                <div className="d-flex gap-1">
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleUseTemplate(template)}
                                    style={{ fontSize: '10px', padding: '2px 6px' }}
                                  >
                                    Использовать
                                  </Button>
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => handleEditTemplate(template)}
                                    style={{ fontSize: '10px', padding: '2px 6px' }}
                                  >
                                    <EditOutlined />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        <Tab
          eventKey="history"
          title={
            <span className="d-flex align-items-center gap-2">
              <FileTextOutlined />
              История
            </span>
          }
        >
          <Card>
            <Card.Body className="p-2">
              {loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : history.length === 0 ? (
                <Alert variant="info" className="mb-0" style={{ fontSize: '12px' }}>
                  Нет отправленных уведомлений
                </Alert>
              ) : (
                <div className="table-responsive">
                  <Table striped bordered hover size="sm">
                    <thead>
                      <tr>
                        <th style={{ fontSize: '12px' }}>Получатель</th>
                        <th style={{ fontSize: '12px' }}>Заголовок</th>
                        <th style={{ fontSize: '12px' }}>Сообщение</th>
                        <th style={{ fontSize: '12px' }}>Тип</th>
                        <th style={{ fontSize: '12px' }}>Статус</th>
                        <th style={{ fontSize: '12px' }}>Дата</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.slice(0, 100).map((item) => (
                        <tr key={item.id}>
                          <td style={{ fontSize: '12px' }}>
                            {item.user?.login || item.userId || '—'}
                            {item.sender?.login && <span className="text-muted ms-1"> (от {item.sender.login})</span>}
                          </td>
                          <td style={{ fontSize: '12px' }}>
                            {item.title || '—'}
                          </td>
                          <td style={{ fontSize: '12px' }}>
                            {item.message || '—'}
                          </td>
                          <td>
                            <Badge bg={getTypeBadge(item.type)} style={{ fontSize: '10px' }}>
                              {item.type || 'info'}
                            </Badge>
                          </td>
                          <td>
                            <Badge bg={item.read ? 'success' : 'warning'} style={{ fontSize: '10px' }}>
                              {item.read ? 'Прочитано' : 'Не прочитано'}
                            </Badge>
                          </td>
                          <td style={{ fontSize: '11px' }}>
                            {formatDate(item.created_at || item.createdAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* Модальное окно отправки */}
      <Modal show={showSendModal} onHide={() => setShowSendModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Отправить уведомление</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Получатели</Form.Label>
              <div style={{ zIndex: 9999, position: 'relative' }}>
                <Select
                  mode="multiple"
                  allowClear
                  style={{ width: '100%' }}
                  placeholder="Выберите пользователей"
                  value={sendForm.userIds}
                  onChange={(values) => setSendForm({ ...sendForm, userIds: values })}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={users.map(u => ({
                    value: u.id,
                    label: `${u.login} ${u.email ? `(${u.email})` : ''}`
                  }))}
                  getPopupContainer={(triggerNode) => triggerNode.parentNode}
                />
              </div>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Тип</Form.Label>
              <Form.Select
                value={sendForm.type}
                onChange={(e) => setSendForm({ ...sendForm, type: e.target.value })}
              >
                <option value="info">Информация</option>
                <option value="success">Успех</option>
                <option value="warning">Предупреждение</option>
                <option value="error">Ошибка</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Заголовок</Form.Label>
              <Form.Control
                type="text"
                value={sendForm.title}
                onChange={(e) => setSendForm({ ...sendForm, title: e.target.value })}
                placeholder="Заголовок уведомления"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Сообщение</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={sendForm.message}
                onChange={(e) => setSendForm({ ...sendForm, message: e.target.value })}
                placeholder="Текст уведомления"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSendModal(false)}>
            Отмена
          </Button>
          <Button variant="primary" onClick={handleSend}>
            Отправить
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Модальное окно массовой рассылки */}
      <Modal show={showBulkModal} onHide={() => setShowBulkModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Массовая рассылка</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Фильтр получателей</Form.Label>
              <Form.Select
                value={bulkForm.filter}
                onChange={(e) => setBulkForm({ ...bulkForm, filter: e.target.value })}
              >
                <option value="all">Все пользователи</option>
                <option value="active">Только активные</option>
                <option value="low_balance">С низким балансом</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Тип</Form.Label>
              <Form.Select
                value={bulkForm.type}
                onChange={(e) => setBulkForm({ ...bulkForm, type: e.target.value })}
              >
                <option value="info">Информация</option>
                <option value="success">Успех</option>
                <option value="warning">Предупреждение</option>
                <option value="error">Ошибка</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Заголовок</Form.Label>
              <Form.Control
                type="text"
                value={bulkForm.title}
                onChange={(e) => setBulkForm({ ...bulkForm, title: e.target.value })}
                placeholder="Заголовок уведомления"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Сообщение</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={bulkForm.message}
                onChange={(e) => setBulkForm({ ...bulkForm, message: e.target.value })}
                placeholder="Текст уведомления"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBulkModal(false)}>
            Отмена
          </Button>
          <Button variant="primary" onClick={handleBulkSend}>
            Отправить
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Модальное окно шаблона */}
      <Modal show={showTemplateModal} onHide={() => setShowTemplateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingTemplate ? 'Редактировать шаблон' : 'Создать шаблон'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Название</Form.Label>
              <Form.Control
                type="text"
                value={templateForm.name}
                onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                placeholder="Название шаблона"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Заголовок</Form.Label>
              <Form.Control
                type="text"
                value={templateForm.title}
                onChange={(e) => setTemplateForm({ ...templateForm, title: e.target.value })}
                placeholder="Заголовок уведомления"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Сообщение</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={templateForm.message}
                onChange={(e) => setTemplateForm({ ...templateForm, message: e.target.value })}
                placeholder="Текст уведомления (можно использовать переменные: {name}, {balance}, etc.)"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Переменные (через запятую)</Form.Label>
              <Form.Control
                type="text"
                value={templateForm.variables}
                onChange={(e) => setTemplateForm({ ...templateForm, variables: e.target.value })}
                placeholder="name, balance, email"
              />
              <Form.Text className="text-muted">
                Список переменных, которые можно использовать в шаблоне
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTemplateModal(false)}>
            Отмена
          </Button>
          <Button variant="primary" onClick={handleSaveTemplate}>
            Сохранить
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Notifications;

